import Gooey, {
    mount,
    Component,
    calc,
    field,
    Field,
    model,
    Model,
    Dyn,
    dynGet,
} from '@srhazi/gooey';
import './web-components.css';

function webComponent<
    TPropKeys extends string,
    TShadowMode extends 'open' | 'closed' | undefined = undefined
>(
    Component: Component<
        TShadowMode extends undefined
            ? {
                  [Key in TPropKeys]: Dyn<string | undefined>;
              }
            : {
                  [Key in TPropKeys]: Dyn<string | undefined>;
              } & {
                  children?: JSX.Node | JSX.Node[];
              }
    >,
    config: {
        name: string;
        attrs: TPropKeys[];
        shadowMode?: TShadowMode;
        delegatesFocus?: boolean;
    }
): void {
    class GooeyElement extends HTMLElement {
        #unmount: (() => void) | undefined;
        #model: Model<any>;
        #mountRoot: Node;
        static observedAttributes = config.attrs;

        constructor() {
            super();
            const props: Record<string, string | undefined> = {};
            config.attrs.forEach((attr) => {
                props[attr] = undefined;
            });
            this.#model = model(props);
            if (config.shadowMode) {
                const shadow = this.attachShadow({
                    mode: config.shadowMode,
                    delegatesFocus: config.delegatesFocus,
                });
                shadow.adoptedStyleSheets = Array.from(
                    document.styleSheets
                ).map((stylesheet) => {
                    const cs = new CSSStyleSheet();
                    cs.replace(
                        Array.from(stylesheet.cssRules)
                            .map((rule) => rule.cssText)
                            .join('\n')
                    );
                    return cs;
                });
                this.#mountRoot = shadow;
            } else {
                this.#mountRoot = this;
            }
        }

        connectedCallback() {
            const props = {} as Record<TPropKeys, Field<string>>;
            config.attrs.forEach((key) => {
                props[key] = model.field(this.#model, key);
            });
            if (config.shadowMode === undefined) {
                const children = Array.from(this.childNodes);
                this.replaceChildren();
                this.#unmount = mount(
                    this.#mountRoot as Element, // TODO: allow ShadowRoot to be here
                    <Component {...props}>{children}</Component>
                );
            } else {
                this.#unmount = mount(
                    this.#mountRoot as Element, // TODO: allow ShadowRoot to be here
                    <Component {...props} />
                );
            }
        }

        disconnectedCallback() {
            this.#unmount?.();
        }

        adoptedCallback() {}

        attributeChangedCallback(
            name: string,
            oldValue: string,
            newValue: string
        ) {
            this.#model[name] = newValue;
        }
    }
    customElements.define(config.name, GooeyElement);
}

const App = () => {
    return (
        <>
            <style>{`div { background-color: revert; color: red; }`}</style>
            <div>
                One: <slot name="one">missing</slot>
            </div>
            <div>
                Two: <slot name="two">missing</slot>
            </div>
            <div>
                Rest: <slot>missing</slot>
            </div>
        </>
    );
};

webComponent(App, {
    name: 'my-shadow-app',
    attrs: [],
    shadowMode: 'closed',
});

const App2 = ({
    name,
    children,
}: {
    name: Dyn<string | undefined>;
    children?: JSX.Node | JSX.Node[];
}) => {
    return (
        <>
            <div>Children: {children}</div>
        </>
    );
};

webComponent(App2, {
    name: 'my-nonshadow-app',
    attrs: ['name'],
});
